import {
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExButton,
    ExIcon,
    ExPill,
    PillColor,
    PillSize,
} from '@boomi/exosphere';
import { useState } from 'react';
import { useApp } from '../common/AppProvider';
import ConfirmAction from '../common/ConfirmAction';
import FlipCard from '../common/FlipCard';
import TextWithArrows from '../common/TextWithArrows';
import { translations } from '../translations/translations';
import type { AuthenticationStatusResponse } from '../types/types';
import { stringReplace } from '../utils/utils';
import type { ConnectorWithTasks } from './Connections';

import './authenticated-connector.css';
import IconCircle from '../common/IconCircle';

interface Props {
    connector: ConnectorWithTasks;
    authenticationStatus: AuthenticationStatusResponse;
    onDelete: (connectorId: string) => void;
}

const AuthenticatedConnector = ({ connector, authenticationStatus, onDelete }: Props) => {
    const { language } = useApp();

    const [showPermissions, setShowPermissions] = useState<boolean>(false);
    const [showTasks, setShowTasks] = useState<boolean>(false);
    const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(null);

    const authenticatedPermissions = () => {
        const authenticatedPermissionIds = Object.keys(
            authenticationStatus[connector.id] || {},
        ).filter((permissionId) => authenticationStatus[connector.id][permissionId]);

        const triggerNames = Object.values(connector.triggers)
            .filter((trigger) => authenticatedPermissionIds.includes(trigger.id))
            .map((trigger) => trigger.name);

        const actionNames = Object.values(connector.actions)
            .filter((action) => authenticatedPermissionIds.includes(action.id))
            .map((action) => action.name);

        return [...triggerNames, ...actionNames];
    };

    const handleClickPermissions = () => {
        if (authenticatedPermissions().length) {
            setShowPermissions(!showPermissions);
            setShowTasks(false);
        }
    };

    const handleClickTasks = () => {
        if (connector.tasks.length) {
            setShowTasks(!showTasks);
            setShowPermissions(false);
        }
    };

    const handleClickDelete = () => {
        setSelectedConnectorId(connector.id);
        setShowPermissions(false);
        setShowTasks(false);
    };

    const handleClickConfirm = () => {
        onDelete(selectedConnectorId!);
        setSelectedConnectorId(null);
    };

    const handleClickCancel = () => {
        setSelectedConnectorId(null);
    };

    const getRelatedTaskNumber = (numberOfTasks: number) => {
        if (numberOfTasks === 1) {
            return translations[language].USER_CONNECTIONS_one_related_tasks_number;
        }

        if (numberOfTasks > 1) {
            return stringReplace(
                translations[language].USER_CONNECTIONS_multi_related_tasks_number,
                numberOfTasks.toString(),
            );
        }

        return '';
    };

    const getRelatedTaskText = (numberOfTasks: number) => {
        if (numberOfTasks >= 1) {
            return translations[language].USER_CONNECTIONS_related_tasks_text;
        }

        return '';
    };

    return (
        <li className="authenticated-connector">
            <FlipCard
                flipOver={selectedConnectorId === connector.id}
                front={
                    <div className="connector-layout ta-tile">
                        <section className="connector-tile-content">
                            <IconCircle connector={connector} addGridArea />

                            <span className="connector-name title lg">{connector.displayName}</span>
                            <span className="connector-description text sm">
                                {connector.description}
                            </span>

                            <section className="connector-triggers">
                                <ExPill
                                    interactive
                                    icon="Secure"
                                    size={PillSize.REGULAR}
                                    color={showPermissions ? PillColor.BLUE : PillColor.GRAY}
                                    onClick={handleClickPermissions}
                                    selected={showPermissions}
                                >
                                    {`${translations[language].USER_CONNECTIONS_permissions} (${
                                        authenticatedPermissions().length
                                    })`}
                                </ExPill>

                                <ExPill
                                    interactive
                                    icon="Paste"
                                    size={PillSize.REGULAR}
                                    color={showTasks ? PillColor.BLUE : PillColor.GRAY}
                                    onClick={handleClickTasks}
                                    selected={showTasks}
                                >
                                    {`${translations[language].USER_CONNECTIONS_tasks} (${connector.tasks.length})`}
                                </ExPill>
                            </section>

                            <ExButton
                                className="delete-action exo-button-action"
                                flavor={ButtonFlavor.RISKY}
                                size={ButtonSize.LARGE}
                                type={ButtonType.TERTIARY}
                                onClick={handleClickDelete}
                                title={`${translations[language].COMMON_delete} ${connector.displayName}`}
                            >
                                <ExIcon icon="trash" hide-browser-tooltip />
                            </ExButton>
                        </section>

                        <section className="connector-extra-content">
                            {showPermissions && (
                                <div className="connector-permission-list">
                                    {authenticatedPermissions().map((itemName) => {
                                        return (
                                            <span
                                                key={itemName}
                                                className="connector-permission text sm"
                                            >
                                                {itemName}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {showTasks && (
                                <div className="connector-tasks-list">
                                    {connector.tasks.map((task) => (
                                        <span key={task.id} className="connector-task text sm">
                                            <TextWithArrows text={task.displayName} />
                                        </span>
                                    ))}
                                </div>
                            )}
                        </section>
                    </div>
                }
                back={
                    <ConfirmAction
                        messages={[
                            <p key={`${connector.id}_text`} className="confirmation-text">
                                {stringReplace(
                                    translations[language].USER_CONNECTIONS_delete_text,
                                    connector.displayName,
                                )}
                            </p>,
                            <p key={`${connector.id}_subtext`} className="confirmation-subtext">
                                {getRelatedTaskNumber(connector.tasks.length)}
                                {getRelatedTaskText(connector.tasks.length)}
                            </p>,
                        ]}
                        onConfirm={handleClickConfirm}
                        onCancel={handleClickCancel}
                    />
                }
            />
        </li>
    );
};

export default AuthenticatedConnector;
